import { createHook, createStore, type BoundActions, type HookFunction } from 'react-sweet-state';

import { teamsClient } from '@atlaskit/teams-client/client';
import type { EditableUserFields, TeamsClientUser as User } from '@atlaskit/teams-client/user';

import type { State, StoreApi } from './types';

const CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes
const CACHE_MAX_SIZE = 5;
const initialState: State = {};

export const actions = {
	getUser:
		(userId: string, isRefetching?: boolean) =>
		async ({ getState, setState }: StoreApi): Promise<void> => {
			const userState = getState()[userId];

			// Do not fetch the user if it is still in cache
			// Or it is loading
			// Or it is refetching
			if (!isRefetching && userState && (userState.isLoading || userState.exp > Date.now())) {
				return;
			}

			// Remove the oldest user from the cache if the cache is full
			const userIds = Object.keys(getState());
			if (userIds.length >= CACHE_MAX_SIZE) {
				const mutableState = { ...getState() };
				const oldestUserId = userIds.reduce((oldest, current) =>
					mutableState[current].exp < mutableState[oldest].exp ? current : oldest,
				);

				delete mutableState[oldestUserId];
				setState({
					...mutableState,
				});
			}

			setState({
				[userId]: {
					isLoading: true,
					error: undefined,
					user: undefined,
					exp: -1,
				},
			});

			try {
				// AGG user query does not verify user’s org membership,
				// it can provide user info to users who are outside the org.
				// To apply proper restrictions on visibility, we need to check if the user is present in the site.
				// Read more in https://hello.atlassian.net/wiki/spaces/PTC/pages/3492140773/LDR+Replace+pf-directory+CloudUser+with+AGG+User
				const userCheckPromise = teamsClient.getUserInSiteUserBase(userId);
				const userQueryPromise = teamsClient.queryAGGUser(userId);

				const checkUserPresentInSiteRes = await userCheckPromise;

				if (!checkUserPresentInSiteRes.isPresent) {
					setState({
						[userId]: {
							isLoading: false,
							exp: -1,
						},
					});
					return;
				}

				const user = await userQueryPromise;

				setState({
					[userId]: {
						isLoading: false,
						exp: Date.now() + CACHE_MAX_AGE,
						user: {
							...user,
							fullName: user.name,
							title: user.extendedProfile?.jobTitle,
							timezone: user.zoneinfo,
							locale: user.locale,
							location: user.extendedProfile?.location,
							companyName: user.extendedProfile?.organization,
							department: user.extendedProfile?.department,
							avatarUrl: user.picture,
							status: user.accountStatus,
							isBot: user.__typename === 'AppUser',
							userType: 'user',
							phoneNumber:
								user.extendedProfile?.phoneNumbers && user.extendedProfile?.phoneNumbers[0]?.value,
							isAgent: user.appType === 'agent',
						},
					},
				});
			} catch (error) {
				setState({
					[userId]: {
						error: error instanceof Error ? error : new Error('Unknown error'),
						isLoading: false,
						exp: -1,
					},
				});
			}
		},

	updateUserInfo:
		(userId: string, fieldKey: keyof EditableUserFields, value: string) =>
		async ({ getState, setState }: StoreApi): Promise<void> => {
			const userState = getState()[userId];
			if (!userState || !userState.user) {
				return;
			}

			const updatedUser = {
				...userState.user,
				[fieldKey]: value,
			};

			setState({
				[userId]: {
					...userState,
					user: updatedUser,
				},
			});

			try {
				await teamsClient.updateUser({
					id: userId,
					[fieldKey]: value,
				});
			} catch (error) {
				setState({
					[userId]: {
						...userState,
						user: userState.user,
					},
				});
			}
		},
	/**
	 * Expose a method to set user data
	 * Useful when we want to update user data that needs relatively long time in BE to sync
	 */
	setUserData:
		(userId: string, data: Partial<User>) =>
		({ getState, setState }: StoreApi): void => {
			const userState = getState()[userId];
			if (!userState || !userState.user) {
				return;
			}

			setState({
				[userId]: {
					...userState,
					user: userState.user && {
						...userState.user,
						...data,
					},
				},
			});
		},
};

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'User',
});

export const useUser: HookFunction<
	State,
	BoundActions<
		State,
		{
			getUser: (
				userId: string,
				isRefetching?: boolean,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			updateUserInfo: (
				userId: string,
				fieldKey: keyof EditableUserFields,
				value: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			/**
			 * Expose a method to set user data
			 * Useful when we want to update user data that needs relatively long time in BE to sync
			 */
			setUserData: (
				userId: string,
				data: Partial<User>,
			) => ({ getState, setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(Store);
